// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DynamicThresholdGrid.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import DashStyleDropdown from 'share/components/controls/Izenda.DashStyleDropdown';
import connect from 'share/enhancers/Izenda.Connect';
import Button from 'share/components/controls/Izenda.Button';
import DropDown from 'share/components/controls/Izenda.DropDown';
import Input from 'share/components/controls/Izenda.Input';
import ColorPicker from 'share/components/controls/Izenda.ColorPicker';
import NumericInput from 'share/components/controls/Izenda.NumericInput';
import CONSTANT from 'logic/data/Izenda.ThresholdGridConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import UICONSTANTS from 'logic/data/Izenda.UIConstants';
import { observable } from 'mobx';
import ThresholdsDataObject from 'logic/models/Izenda.ThresholdsDataObject';
import DateTimePickerByDateElement from 'share/components/controls/Izenda.DateTimePickerByDateElement';
import { ContainerName, isChartType } from 'logic/data/Izenda.ReportPartChartConstants';
import AddFieldSetting from './Izenda.AddFieldSetting';
import LabelSettingPopup from './Izenda.ThresholdLabelSettingsPopup';
import action from './Izenda.DynamicThresholdGridAction';

import { isNil, get, find } from 'lodash/fp';

const ValidateInput = Validator()(Input);
const ValidateAddFieldSetting = Validator()(AddFieldSetting);
const TABLE_DEF = CONSTANT.DYNAMIC_TABLE_DEF;
const TABLE_CONTAINER_STYLE = CONSTANT.TABLE_CONTAINER_STYLE;
const THRESHOLD_TABLE_HEIGHT = 170;

@connect(action)
export default class DynamicThresholdGrid extends Component {
  @observable sortDirs: Array<any> = [];

  /**
   * Render column type of threshold ( dynamic - static)
   */
  renderType = (data: ThresholdsDataObject): void => {
    /**
     * change threshold type value
     */
    const onChange = e => {
      data.updateThresholdType(e.value);
      data.updateValue(null);
      data.clearDynamicValue();
    };

    return (
      <DropDown
        labelField='label'
        valueField='value'
        options={CONSTANT.THRESLOD_TYPE}
        value={data.thresholdType}
        onChange={onChange}
      />
    );
  };

  /**
   * Render column type of threshold ( dynamic - static)
   */
  renderMetric = (data: ThresholdsDataObject) => {
    const { options } = this.props;

    /**
     * Change metric value
     */
    const onChange = e => {
      data.updateFieldMappingAlias(e.value);
      this.onChangeSettingValue();
    };

    return (
      <DropDown
        labelField='label'
        value={data.fieldMappingAlias}
        valueField='value'
        options={options}
        onChange={onChange}
      />
    );
  };

  /**
   * Close popup
   */
  onClosing = () => {
    this.props.onReject();
  };
  /**
   * Render threshold label
   */
  renderLabel = (data: ThresholdsDataObject, key: string, rowIndex: number) => {
    const props = this.props;
    const rowKey = rowIndex + props.axis;

    const constraints = {
      presence: function() {
        return {
          presence: true,
          message: CONSTANT.MESSAGE.M06
        };
      }
    };

    const openPopup = () => {
      props.openPopup(LabelSettingPopup, {
        onChange: onChangeLabelSetting,
        value: data.labelSetting
      });
    };

    const updateLabel = e => {
      data.updateLabel(e.target.value);
      this.onChangeSettingValue();
    };

    const onChangeLabelSetting = e => {
      data.updateLabelSetting(e.value);
      this.onChangeSettingValue();
    };

    const className = data.thresholdType !== 'dynamic' ? 'label-input' : '';
    return (
      <div>
        <div className={className}>
          <ValidateInput
            field={rowKey}
            group={rowIndex + key}
            validations={constraints}
            className='form-control'
            onChange={updateLabel}
            value={data.label}
          />
        </div>
        {data.thresholdType !== 'dynamic' && (
          <a>
            <span className='icon-setting' onClick={openPopup} />
          </a>
        )}
      </div>
    );
  };

  /**
   * Apply change setting
   */
  onChangeSettingValue = () => {
    this.props.onChange({ value: this.props.thresholdData }, ContainerName.DYNAMIC_THRESHOLD);
  };

  /**
   * Render value
   */
  renderValue = (data: ThresholdsDataObject, key: string, rowIndex: number) => {
    const {
      props,
      props: { options, reportPartContent, dataTree }
    } = this;
    const currentXAxis = find(axis => axis.value === data.fieldMappingAlias, options);
    const rowKey = rowIndex + this.props.axis;

    const constraints = {
      presence: function() {
        return {
          presence: true,
          message: CONSTANT.MESSAGE.M05
        };
      }
    };

    const onOk = changeData => {
      props.addDynamicThreholdsField(data, changeData);
    };

    const onChangeInput = e => {
      if (!String.isPatternFormat(e.target.value, CONSTANT.NUMBER_REGEX)) {
        return;
      }
      data.updateValue(e.target.value);
      this.onChangeSettingValue(true);
    };

    const onChangeDateTime = value => {
      data.updateValue(value);
      this.onChangeSettingValue(true);
    };

    if (data.thresholdType === 'dynamic') {
      return (
        <ValidateAddFieldSetting
          openPopup={props.openPopup}
          localization={props.localization}
          dataTree={dataTree}
          field={rowKey}
          group={rowIndex + key}
          validations={constraints}
          reportPartContent={reportPartContent}
          onOk={onOk}
          value={data.value}
          data={data}
        />
      );
    } else {
      return currentXAxis && !isNil(currentXAxis.dateTimeFormatData) ? (
        <DateTimePickerByDateElement
          groupByDateTime={get('dateTimeFormatData.groupBy', currentXAxis)}
          onChange={onChangeDateTime}
          value={data.value}
        />
      ) : (
        <ValidateInput
          field={rowKey}
          group={rowIndex + key}
          validations={constraints}
          className='form-control'
          onChange={onChangeInput}
          value={data.value}
        />
      );
    }
  };

  /**
   * Render dash style
   */
  renderDashStyle = (data: ThresholdsDataObject) => {
    const props = this.props;

    const onChange = function(changeData) {
      props.onChangeDashType(data, changeData);
    };

    return <DashStyleDropdown value={data.dashStyle} onChange={onChange} />;
  };

  /**
   * Render dash style
   */
  renderColor = (data: ThresholdsDataObject) => {
    const onChange = e => {
      data.updateColor(e);
      this.onChangeSettingValue();
    };
    return <ColorPicker color={data.color} onChange={onChange} />;
  };

  /**
   * Render dash style
   */
  renderThickness = (data: ThresholdsDataObject) => {
    const onChange = value => {
      data.updateThickness(value);
      this.onChangeSettingValue();
    };
    return (
      <div className='input-group'>
        <NumericInput
          min={UICONSTANTS.borderWidthMin}
          max={UICONSTANTS.borderWidthMax}
          onChange={onChange}
          value={data.thickness}
        />
        <span className='input-group-addon'>px</span>
      </div>
    );
  };

  /**
   * Render visible threshold column
   */
  renderVisible = (data: ThresholdsDataObject, key: string, rowIndex: number) => {
    const onChange = e => {
      data.updateVisible(e.target.checked);
      this.onChangeSettingValue();
    };
    return (
      <ValidateInput
        group={rowIndex + key}
        type='checkbox'
        onChange={onChange}
        checked={data.visible}
        value={data.visible}
      />
    );
  };

  /**
   * Render action column
   */
  renderAction = (data: ThresholdsDataObject, key: string, rowIndex: number) => {
    const {
      props,
      props: { localization }
    } = this;
    const deleteFn = props.deleteThreshold.bind(null, rowIndex);
    const copyFn = props.copyThreshold.bind(null, rowIndex);

    return (
      <div>
        <a>
          <span
            title={localization.getText('REPORTDESIGNER_LABEL_COPY_SETTING')}
            className='icon-copy'
            onClick={copyFn}
          />
        </a>
        <a>
          <span
            title={localization.getText('REPORTDESIGNER_LABEL_DELETE_SETTING')}
            className='icon-cancel'
            onClick={deleteFn}
          />
        </a>
      </div>
    );
  };

  render() {
    const { localization, staticThreshold, thresholdData, chartType } = this.props;
    const renderDynamic = !isChartType(chartType, CHART_TYPE.Scatter, CHART_TYPE.Bubble) && !staticThreshold;

    return (
      <section style={TABLE_CONTAINER_STYLE}>
        <Table
          dataSource={thresholdData}
          onSortChange={this.props.onSortChange}
          draggableTable={false}
          sortInfo={this.sortDirs}
          minHeight={THRESHOLD_TABLE_HEIGHT}
          maxHeight={THRESHOLD_TABLE_HEIGHT}
        >
          {renderDynamic && <Column {...TABLE_DEF['type']} cell={this.renderType} />}
          <Column {...TABLE_DEF['field']} cell={this.renderMetric} />
          <Column {...TABLE_DEF['label']} cell={this.renderLabel} />
          <Column {...TABLE_DEF['value']} cell={this.renderValue} />
          <Column {...TABLE_DEF['dashStyle']} cell={this.renderDashStyle} />
          <Column {...TABLE_DEF['color']} cell={this.renderColor} />
          <Column {...TABLE_DEF['thickness']} cell={this.renderThickness} />
          <Column {...TABLE_DEF['visible']} cell={this.renderVisible} />
          <Column {...TABLE_DEF['action']} cell={this.renderAction} />
        </Table>
        <div className='row'>
          <div className='col-md-12 text-right add-setting'>
            <Button label={localization.getText('COMMON_LABEL_ADD_SETTING')} onClick={this.props.addNewThreshold} />
          </div>
        </div>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.DynamicThresholdGrid.js